"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";

import { replayMetricsApi, parseMatchTeamStats, parseTournamentTeamStats } from "./validation";
import type { ReplayMetricValue } from "../model/types";


/**
 * KAN-792: командная статистика по реплеям — два среза одного DTO (KAN-790).
 *
 * Типы рукописные: эндпоинты новые и в пинованный swagger ещё не попали. После
 * деплоя на тест и `make api-sync-test` переводятся на сгенерированные, как это
 * было с лентой таймлайна.
 */

export interface ReplayTeamTotals {
  kills: number;
  deaths: number;
  assists: number;
  gold: number;
  damageToChampions: number;
  damageTaken: number;
  cs: number;
  visionScore: number;
  wardsPlaced: number;
  wardsKilled: number;
  controlWardsBought: number;
  turrets: number;
}

/** null у поля — объекта нет в сырье этого патча; 0 — настоящий ноль. */
export interface ReplayTeamObjectives {
  dragons: number | null;
  barons: number | null;
  heralds: number | null;
  voidgrubs: number | null;
  inhibitors: number | null;
}

/** KAN-793: строка игрока в командном табло одной игры. */
export interface ReplayTeamPlayer {
  userId: string | null;
  riotIdGameName: string | null;
  riotIdTagLine: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  champion: string | null;
  position: string | null;
  kills: number;
  deaths: number;
  assists: number;
  cs: number;
  gold: number;
  damageToChampions: number;
  damageTaken: number;
  visionScore: number;
  wardsPlaced: number;
  level: number;
}

/** KAN-781: пара чемпионов команды. synergyKind !== null — пара из справочника связок. */
export interface ReplayChampionPair {
  championA: string;
  championB: string;
  games: number;
  wins: number;
  synergyKind: string | null;
}

export interface ReplayTeamMetrics {
  teamId: string | null;
  teamName: string | null;
  /** "100" — синие, "200" — красные. null у агрегата за турнир: команда играла с обеих сторон. */
  teamSide: string | null;
  matchId: string;
  /** null — агрегат по нескольким играм. */
  gameId: number | null;
  gamesTotal: number;
  wins: number;
  durationSeconds: number | null;
  playerRows: number;
  totals: ReplayTeamTotals;
  objectives: ReplayTeamObjectives;
  metrics: ReplayMetricValue[];
  /** Состав стороны. Пусто в агрегате за турнир: между играми состав меняется. */
  players: ReplayTeamPlayer[];
  /** Связки. Только в агрегате за турнир: по одной игре это просто состав. */
  championPairs: ReplayChampionPair[];
}

/**
 * Командная статистика по каждой игре матча — обе стороны, по строке на сторону.
 * Пустой массив означает, что реплеев у матча нет: штатное состояние, витрина
 * в этом случае не показывается.
 */
export function useMatchTeamStats(matchId: string | undefined) {
  return useQuery({
    queryKey: ["replay-metrics", "team", "match", matchId],
    enabled: Boolean(matchId),
    queryFn: async () => {
      return parseMatchTeamStats((await replayMetricsApi.getMatchTeamStats(matchId!)).data);
    },
  });
}

/** Командная статистика за все игры турнира — по строке на команду. */
// KAN-993: опции отдельно от хука — для прогрева кэша страницей итогов.
export const tournamentTeamStatsQuery = (tournamentId: string) =>
  queryOptions({
    queryKey: ["replay-metrics", "team", "tournament", tournamentId],
    queryFn: async () => parseTournamentTeamStats((await replayMetricsApi.getTournamentTeamStats(tournamentId)).data),
  });

export function useTournamentTeamStats(tournamentId: string | undefined) {
  return useQuery({ ...tournamentTeamStatsQuery(tournamentId ?? ""), enabled: Boolean(tournamentId) });
}
